@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.setup

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.ScreenManager
import org.example.project.registrierung.NetzwerkLoginStyles
import org.example.project.service.PasswordPolicy
import org.example.project.service.RollenService
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.LogoSettings
import org.example.project.settings.PasswordRequirementHint
import org.example.project.settings.UISettings
import org.example.project.utils.All
import org.example.project.utils.All.AllNames.names
import org.example.project.utils.DashboardVerlaufHintergrund
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Header
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Main
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.Event

private const val SYS_SETUP_STEP_COUNT = 6
private const val SYS_SETUP_ROLE_ID = "1"

private fun sysSetupLabel(fieldId: String): String = when (fieldId) {
    "geschaeftsfuehrer" -> "Gründer"
    else -> names[fieldId] ?: fieldId
}
private fun sysSetupFullName(vorname: String, nachname: String): String =
    listOf(vorname.trim(), nachname.trim()).filter { it.isNotBlank() }.joinToString(" ")

private fun sysSetupShortcut(vorname: String, nachname: String): String {
    fun twoLetters(value: String): String {
        val cleaned = value.filter { it.isLetter() }
        return when {
            cleaned.isEmpty() -> ""
            cleaned.length == 1 -> cleaned.uppercase()
            else -> cleaned.substring(0, 1).uppercase() + cleaned.substring(1, 2).lowercase()
        }
    }

    return (twoLetters(vorname) + twoLetters(nachname)).ifBlank { "AD" }
}

private fun All.sysFieldValue(fieldId: String): String = when (fieldId) {
    "firma" -> firma
    "geschaeftsfuehrer" -> geschaeftsfuehrer
    "vorname" -> vorname
    "nachname" -> nachname
    "position" -> position
    "webseite" -> webseite
    "gegruendetAm" -> gegruendetAm
    "strasse" -> strasse
    "hausnummer" -> hausnummer
    "plz" -> plz
    "ort" -> ort
    "email" -> email
    "telefon" -> telefon
    "mobil" -> mobil
    else -> ""
}

private fun All.withSysField(fieldId: String, value: String): All = when (fieldId) {
    "firma" -> copy(firma = value)
    "geschaeftsfuehrer" -> copy(geschaeftsfuehrer = value)
    "vorname" -> copy(vorname = value)
    "nachname" -> copy(nachname = value)
    "position" -> copy(position = value)
    "webseite" -> copy(webseite = value)
    "gegruendetAm" -> copy(gegruendetAm = value)
    "strasse" -> copy(strasse = value)
    "hausnummer" -> copy(hausnummer = value)
    "plz" -> copy(plz = value)
    "ort" -> copy(ort = value)
    "email" -> copy(email = value)
    "telefon" -> copy(telefon = value)
    "mobil" -> copy(mobil = value)
    else -> this
}

private fun sysSetupAutocomplete(fieldId: String): String = when (fieldId) {
    "firma" -> "organization"
    "vorname" -> "given-name"
    "nachname" -> "family-name"
    "strasse" -> "address-line1"
    "hausnummer" -> "address-line2"
    "plz" -> "postal-code"
    "ort" -> "address-level2"
    "email" -> "email"
    "telefon" -> "tel"
    "mobil" -> "tel"
    "webseite" -> "url"
    else -> "off"
}

@Composable
fun SYSlumaticasetup(onComplete: () -> Unit) {
    Style(cssRules = NetzwerkLoginStyles.cssRules)
    DashboardVerlaufHintergrund()

    val scope = rememberCoroutineScope()
    var step by remember { mutableStateOf(0) }
    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var unternehmen by remember { mutableStateOf(All(id = "unternehmenstamm")) }
    var zugangEmail by remember { mutableStateOf("") }
    var passwort by remember { mutableStateOf("") }
    var passwortWdh by remember { mutableStateOf("") }
    var setupRoleName by remember { mutableStateOf("") }

    LaunchedEffect(Unit) {
        val loaded = SetupApi.loadSysBootstrapState()
        if (loaded != null) unternehmen = loaded
        val firmenId = SetupNodeApi.loadNodeFirmenId()
        if (firmenId.isNotBlank()) {
            val registerFirma = SetupNodeApi.loadRegisterCompany(firmenId)
            if (registerFirma != null) {
                unternehmen = unternehmen.copy(
                    firma = unternehmen.firma.ifBlank { registerFirma.firma },
                    strasse = unternehmen.strasse.ifBlank { registerFirma.strasse },
                    hausnummer = unternehmen.hausnummer.ifBlank { registerFirma.hausnummer },
                    plz = unternehmen.plz.ifBlank { registerFirma.plz },
                    ort = unternehmen.ort.ifBlank { registerFirma.ort }
                )
            }
        }
    }

    fun finishSetup() {
        val setupVorname = unternehmen.vorname.trim()
        val setupNachname = unternehmen.nachname.trim()
        val setupUserName = sysSetupFullName(setupVorname, setupNachname)
        val passwordError = PasswordPolicy.error(passwort)
        if (zugangEmail.isBlank() || passwordError != null || passwort != passwortWdh) {
            error = passwordError ?: "Bitte E-Mail und Passwort vollständig ausfüllen."
            return
        }
        if (setupVorname.isBlank() || setupNachname.isBlank()) {
            error = "Bitte Vorname und Nachname eintragen."
            step = 1
            return
        }
        if (setupRoleName.trim().isBlank()) {
            error = "Bitte Rolle eintragen."
            step = 5
            return
        }
        if (loading) return
        loading = true
        error = null
        scope.launch {
            try {
                val roleName = setupRoleName.trim()
                val firmenId = SetupNodeApi.loadNodeFirmenId().ifBlank { "ls1000" }
                val setupUnternehmen = unternehmen.copy(
                    id = "unternehmenstamm",
                    geschaeftsfuehrer = setupUserName
                )
                val userId = "1"
                val setupRole = All(
                    id = SYS_SETUP_ROLE_ID,
                    name = roleName,
                    bezeichnung = roleName,
                    screenBerechtigungen = ScreenManager.allIndexKeys().associateWith { RollenService.ALLES }
                )
                val userEmail = zugangEmail.trim().lowercase()
                val setupUser = All(
                    id = userId,
                    vorname = setupVorname,
                    nachname = setupNachname,
                    name = setupUserName,
                    email = userEmail,
                    kuerzel = sysSetupShortcut(setupVorname, setupNachname),
                    rolle = SYS_SETUP_ROLE_ID,
                    position = roleName,
                    firmenId = firmenId,
                    firma = setupUnternehmen.firma,
                    kunde = setupUnternehmen.firma,
                    zugangDesktop = true,
                    zugangMobil = true,
                    gesperrt = false,
                    datenschutzAkzeptiert = true
                )
                SetupApi.completeSysBootstrap(setupUnternehmen, setupUser, setupRole, passwort)
                    ?.takeIf { it.passwortVerifiziert }
                    ?: error("Passwort konnte nicht gespeichert werden.")
                onComplete()
            } catch (_: Throwable) {
                error = "Setup konnte nicht gespeichert werden."
            } finally {
                loading = false
            }
        }
    }

    fun continueToAddressStep() {
        if (unternehmen.vorname.trim().isBlank() || unternehmen.nachname.trim().isBlank()) {
            error = "Bitte Vorname und Nachname eintragen."
            return
        }
        error = null
        step = 2
    }

    fun continueToRoleStep() {
        val passwordError = PasswordPolicy.error(passwort)
        if (zugangEmail.isBlank() || passwordError != null || passwort != passwortWdh) {
            error = passwordError ?: "Bitte E-Mail und Passwort vollständig ausfüllen."
            return
        }
        error = null
        step = 5
    }

    Header({ classes(NetzwerkLoginStyles.topBar) }) {
        Div({ classes(NetzwerkLoginStyles.headerContent) }) {
            Div({ classes(NetzwerkLoginStyles.centerBlock) }) {
                Img(src = LogoSettings.desktopRegistrationHeaderLogo, attrs = {
                    classes(NetzwerkLoginStyles.centerLogo)
                    style { height(LogoSettings.desktopRegistrationHeaderLogoHeightPx.px) }
                })
            }
        }
    }

    Main({ classes(NetzwerkLoginStyles.mainContent) }) {
        Div({
            classes(NetzwerkLoginStyles.gifWrapper)
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                width(100.percent)
                gap(20.px)
            }
        }) {
            Div({
                style {
                    width(850.px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.Stretch)
                    gap(8.px)
                }
            }) {
                Div({
                    classes(NetzwerkLoginStyles.registerFrame)
                    style {
                        position(Position.Relative)
                        width(100.percent)
                        height(550.px)
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        justifyContent(JustifyContent.SpaceBetween)
                        padding(34.px)
                    }
                }) {
                    if (step > 0) {
                        Img(src = "images/back.svg", attrs = {
                            classes(NetzwerkLoginStyles.navIcon)
                            style { property("left", "42px") }
                            onClick { step-- }
                        })
                    }

                    when (step) {
                        0 -> {
                            SysSetupPanelBody {
                                H2 { Text("Willkommen Tim Stolz, im lumatica Setup") }
                                P { Text("Bitte richte deinen Zugang für das Control Center ein.") }
                                Img(src = LogoSettings.systemSetupHeroLogo, attrs = {
                                    style {
                                        width(74.percent)
                                        property("max-height", "300px")
                                        property("object-fit", "cover")
                                        borderRadius(20.px)
                                        property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
                                    }
                                })
                            }
                            SysSetupPanelActions {
                                LumaticaButton("Setup starten", { step = 1 }, LumaticaButtonVariant.Action, heightPx = 44)
                            }
                        }
                        1 -> {
                            SysSetupPanelBody {
                                H2 { Text("Das Unternehmen Lumatica") }
                                P { Text("Bitte trage die Firmenrelevanten Daten hier ein.") }

                                Div({ classes(NetzwerkLoginStyles.formGrid) }) {
                                    SysSetupFieldInput(InputType.Text, unternehmen.sysFieldValue("firma"), "firma") {
                                        unternehmen = unternehmen.withSysField("firma", it)
                                    }
                                    SysSetupFieldInput(InputType.Text, unternehmen.sysFieldValue("gegruendetAm"), "gegruendetAm") {
                                        unternehmen = unternehmen.withSysField("gegruendetAm", it)
                                    }

                                    SysSetupFieldInput(InputType.Text, unternehmen.sysFieldValue("vorname"), "vorname") {
                                        unternehmen = unternehmen.withSysField("vorname", it)
                                    }
                                    SysSetupFieldInput(InputType.Text, unternehmen.sysFieldValue("nachname"), "nachname") {
                                        unternehmen = unternehmen.withSysField("nachname", it)
                                    }
                                    SysSetupError(error)

                                }
                            }
                            SysSetupPanelActions { LumaticaButton("Weiter", ::continueToAddressStep, LumaticaButtonVariant.Action, heightPx = 42) }
                        }
                        2 -> {
                            SysSetupPanelBody {
                                H2 { Text("Adresse") }
                                P { Text("Wo hat Lumatica ihren Firmensitz?") }

                                Div({ classes(NetzwerkLoginStyles.formGrid) }) {
                                    Div({ classes(NetzwerkLoginStyles.row) }) {
                                        SysSetupFieldInput(InputType.Text, unternehmen.sysFieldValue("strasse"), "strasse") {
                                            unternehmen = unternehmen.withSysField("strasse", it)
                                        }
                                        SysSetupFieldInput(InputType.Text, unternehmen.sysFieldValue("hausnummer"), "hausnummer") {
                                            unternehmen = unternehmen.withSysField("hausnummer", it)
                                        }
                                    }
                                    Div({ classes(NetzwerkLoginStyles.row) }) {
                                        SysSetupFieldInput(InputType.Text, unternehmen.sysFieldValue("plz"), "plz") {
                                            unternehmen = unternehmen.withSysField("plz", it)
                                        }
                                        SysSetupFieldInput(InputType.Text, unternehmen.sysFieldValue("ort"), "ort") {
                                            unternehmen = unternehmen.withSysField("ort", it)
                                        }
                                    }
                                }
                            }
                            SysSetupPanelActions { LumaticaButton("Weiter", { step = 3 }, LumaticaButtonVariant.Action, heightPx = 42) }
                        }
                        3 -> {
                            SysSetupPanelBody {
                                H2 { Text("Kontakt") }
                                P { Text("Bitte fülle die Kontaktdaten deines Unternehmens aus") }

                                Div({ classes(NetzwerkLoginStyles.formGrid) }) {
                                    SysSetupFieldInput(InputType.Email, unternehmen.sysFieldValue("email"), "email") {
                                        unternehmen = unternehmen.withSysField("email", it)
                                    }
                                    SysSetupFieldInput(InputType.Text, unternehmen.sysFieldValue("webseite"), "webseite") {
                                        unternehmen = unternehmen.withSysField("webseite", it)
                                    }
                                    Div({ classes(NetzwerkLoginStyles.row) }) {
                                        SysSetupFieldInput(InputType.Text, unternehmen.sysFieldValue("telefon"), "telefon") {
                                            unternehmen = unternehmen.withSysField("telefon", it)
                                        }
                                        SysSetupFieldInput(InputType.Text, unternehmen.sysFieldValue("mobil"), "mobil") {
                                            unternehmen = unternehmen.withSysField("mobil", it)
                                        }
                                    }
                                }
                            }
                            SysSetupPanelActions { LumaticaButton("Weiter", { step = 4 }, LumaticaButtonVariant.Action, heightPx = 42) }
                        }
                        4 -> {
                            SysSetupPanelBody {
                                H2 { Text("Zugang") }
                                P { Text("Richte deinen Zugang ein.") }

                                Div({ classes(NetzwerkLoginStyles.formGrid) }) {
                                    SysSetupInput(InputType.Email, zugangEmail, "E-Mail-Adresse", "setup-email", "username") { zugangEmail = it }
                                    SysSetupInput(InputType.Password, passwort, "Passwort", "setup-password", "new-password") { passwort = it }
                                    PasswordRequirementHint(passwort)
                                    SysSetupInput(InputType.Password, passwortWdh, "Passwort wiederholen", "setup-password-repeat", "new-password") { passwortWdh = it }
                                    SysSetupError(error)
                                }
                            }
                            SysSetupPanelActions {
                                LumaticaButton(if (loading) "..." else "Weiter", ::continueToRoleStep, LumaticaButtonVariant.Action, enabled = !loading, heightPx = 42)
                            }
                        }
                        5 -> {
                            SysSetupPanelBody {
                                H2 { Text("Bitte lege deine Rolle fest.") }
                                P { Text("Diese Rolle wird exklusiv für dich reserviert.") }

                                Div({ classes(NetzwerkLoginStyles.formGrid) }) {
                                    Div({
                                        style {
                                            width(100.percent)
                                            property("max-width", "360px")
                                            property("margin-left", "auto")
                                            property("margin-right", "auto")
                                        }
                                    }) {
                                        SysSetupInput(InputType.Text, setupRoleName, "", "setup-role", "off") { setupRoleName = it }
                                    }
                                    SysSetupError(error)
                                }
                            }
                            SysSetupPanelActions {
                                LumaticaButton(if (loading) "..." else "Setup abschließen", ::finishSetup, LumaticaButtonVariant.Action, enabled = !loading, heightPx = 42)
                            }
                        }
                    }
                }
                SysSetupProgressTabs(step)
            }
        }
    }
}

@Composable
private fun SysSetupPanelBody(content: @Composable () -> Unit) {
    Div({
        style {
            width(100.percent)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
        }
    }) {
        content()
    }
}

@Composable
private fun SysSetupPanelActions(content: @Composable () -> Unit) {
    Div({
        style {
            width(100.percent)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            gap(12.px)
        }
    }) {
        content()
    }
}

@Composable
private fun SysSetupInput(
    type: InputType<String>,
    value: String,
    label: String,
    fieldName: String,
    autocomplete: String,
    onInput: (String) -> Unit
) {
    Input(type, attrs = {
        classes(NetzwerkLoginStyles.regInput)
        placeholder(label)
        attr("id", fieldName)
        attr("name", fieldName)
        attr("autocomplete", autocomplete)
        value(value)
        onInput { onInput(it.value) }
        ref { element ->
            val input = element as HTMLInputElement
            val sync: (Event) -> Unit = { onInput(input.value) }
            input.addEventListener("change", sync)
            input.addEventListener("blur", sync)
            onDispose {
                input.removeEventListener("change", sync)
                input.removeEventListener("blur", sync)
            }
        }
    })
}

@Composable
private fun SysSetupFieldInput(type: InputType<String>, value: String, fieldId: String, onInput: (String) -> Unit) {
    SysSetupInput(
        type = type,
        value = value,
        label = sysSetupLabel(fieldId),
        fieldName = "setup-$fieldId",
        autocomplete = sysSetupAutocomplete(fieldId),
        onInput = onInput
    )
}

@Composable
private fun SysSetupError(text: String?) {
    if (!text.isNullOrBlank()) {
        P({ classes(NetzwerkLoginStyles.errorParagraph) }) { Text(text) }
    }
}

@Composable
private fun SysSetupProgressTabs(currentStep: Int) {
    Div({
        style {
            width(100.percent)
            display(DisplayStyle.Flex)
            gap(6.px)
            height(10.px)
            alignItems(AlignItems.Center)
        }
    }) {
        repeat(SYS_SETUP_STEP_COUNT) { index ->
            Div({
                style {
                    property("flex", "1")
                    height(3.px)
                    backgroundColor(if (index == currentStep) UISettings.ACCENT else DashboardTheme.BORDER_LIGHT)
                }
            }) {}
        }
    }
}
